import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Table, Button, Popconfirm, Form, Input, Progress, Row, Col,
  Typography, App, Tag, Space, Statistic,
} from "antd";
import { DeleteOutlined, UserAddOutlined, ReloadOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";

const { Title } = Typography;

function formatBytes(bytes) {
  if (!bytes) return "0 GB";
  return (bytes / 1024 / 1024 / 1024).toFixed(1) + " GB";
}

function formatUptime(seconds) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return `${days}d ${hours}h`;
}

export default function Admin() {
  const { user } = useAuth();
  const { message } = App.useApp();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteForm] = Form.useForm();
  const [creatingInvite, setCreatingInvite] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, invitesRes, systemRes] = await Promise.all([
        fetch("/api/admin/users", { credentials: "include" }),
        fetch("/api/admin/invites", { credentials: "include" }),
        fetch("/api/admin/system", { credentials: "include" }),
      ]);
      setUsers((await usersRes.json()).users || []);
      setInvites((await invitesRes.json()).invites || []);
      setSystem(await systemRes.json());
    } catch (err) {
      message.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleDeleteUser(id) {
    const res = await fetch(`/api/admin/users/${id}`, { method: "DELETE", credentials: "include" });
    const data = await res.json();
    if (!res.ok) return message.error(data.error || "Failed to delete user");
    message.success("User deleted");
    loadAll();
  }

  async function handleRevokeInvite(token) {
    await fetch(`/api/admin/invites/${token}`, { method: "DELETE", credentials: "include" });
    message.success("Invite revoked");
    loadAll();
  }

  async function handleCreateInvite(values) {
    setCreatingInvite(true);
    try {
      const res = await fetch("/api/admin/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create invite");
      const link = `${window.location.origin}/accept-invite/${data.invite.token}`;
      message.success({
        content: (
          <span>
            Invite created — <Typography.Text copyable={{ text: link }}>copy link</Typography.Text>
          </span>
        ),
        duration: 10,
      });
      inviteForm.resetFields();
      loadAll();
    } catch (err) {
      message.error(err.message);
    } finally {
      setCreatingInvite(false);
    }
  }

  const userColumns = [
    { title: "Email", dataIndex: "email" },
    { title: "Name", dataIndex: "name" },
    { title: "Tenant", dataIndex: "tenant" },
    {
      title: "Role",
      dataIndex: "isAdmin",
      render: (isAdmin) => (isAdmin ? <Tag color="blue">Admin</Tag> : <Tag>Member</Tag>),
    },
    { title: "Created", dataIndex: "createdAt", render: (d) => new Date(d).toLocaleDateString() },
    {
      title: "",
      dataIndex: "id",
      render: (id) =>
        id === user?.id ? null : (
          <Popconfirm title="Delete this user?" onConfirm={() => handleDeleteUser(id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
    },
  ];

  const inviteColumns = [
    { title: "Email", dataIndex: "email" },
    { title: "Name", dataIndex: "name" },
    { title: "Tenant", dataIndex: "tenant" },
    { title: "Expires", dataIndex: "expiresAt", render: (d) => new Date(d).toLocaleDateString() },
    {
      title: "Link",
      dataIndex: "token",
      render: (token) => {
        const link = `${window.location.origin}/accept-invite/${token}`;
        return (
          <Typography.Text
            copyable={{ text: link, tooltips: ["Copy link", "Copied"] }}
            style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}
          >
            Copy link
          </Typography.Text>
        );
      },
    },
    {
      title: "",
      dataIndex: "token",
      render: (token) => (
        <Popconfirm title="Revoke this invite?" onConfirm={() => handleRevokeInvite(token)} okText="Revoke" okButtonProps={{ danger: true }}>
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Title level={3} style={{ margin: 0 }}>Admin</Title>
        <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>Refresh</Button>
      </Space>

      <Card title="System" style={{ marginBottom: 16 }} loading={!system}>
        {system && (
          <Row gutter={32}>
            <Col span={8}>
              <div style={{ marginBottom: 8 }}>Disk</div>
              <Progress percent={system.disk.usedPercent} status={system.disk.usedPercent > 85 ? "exception" : "normal"} />
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
                {formatBytes(system.disk.usedBytes)} / {formatBytes(system.disk.totalBytes)} used
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 8 }}>Memory</div>
              <Progress percent={system.memory.usedPercent} status={system.memory.usedPercent > 85 ? "exception" : "normal"} />
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
                {formatBytes(system.memory.totalBytes - system.memory.freeBytes)} / {formatBytes(system.memory.totalBytes)} used
              </div>
            </Col>
            <Col span={8}>
              <Statistic title="Uptime" value={formatUptime(system.uptimeSeconds)} />
            </Col>
          </Row>
        )}
      </Card>

      <Card title="Invite a new user" style={{ marginBottom: 16 }}>
        <Form form={inviteForm} layout="inline" onFinish={handleCreateInvite}>
          <Form.Item name="email" rules={[{ required: true, type: "email" }]}>
            <Input placeholder="Email" style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="name">
            <Input placeholder="Name" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="tenant" initialValue="proyapi">
            <Input placeholder="Tenant" style={{ width: 120 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<UserAddOutlined />} loading={creatingInvite}>
              Create invite
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {invites.length > 0 && (
        <Card title="Pending invites" style={{ marginBottom: 16 }}>
          <Table dataSource={invites} columns={inviteColumns} rowKey="token" pagination={false} size="small" />
        </Card>
      )}

      <Card title="Users">
        <Table dataSource={users} columns={userColumns} rowKey="id" pagination={false} size="small" />
      </Card>
    </div>
  );
}
